/* eslint-disable react/prop-types */
import {lazy, memo, Suspense, useCallback, useContext, useEffect, useReducer, useState} from 'react'

import {useDecisionCallback} from '@s-ui/pde'
import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomSpinner, {atomSpinnerOverlayTypes, atomSpinnerTypes} from '@s-ui/react-atom-spinner'
import Context from '@s-ui/react-context'
import IconError from '@s-ui/react-icons/lib/Lineerror'
import MoleculeNotification, {
  moleculeNotificationAutoClose,
  moleculeNotificationPosition,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import FormContactSeller from '@adv-mt/mt-form-contact-seller'
import LeadPhoneCall, {leadPhoneCallTarget, leadPhoneCallType} from '@adv-mt/mt-lead-phone-call'
import {listRelatedAdsContactTypes} from '@adv-mt/mt-list-related-ads'
import IconWhatsApp from '@adv-mt/mt-svg-icons/lib/Whatsapp'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'

import {reducer, reducerActions, reducerInitialState} from '../../reducer.js'
import {getIsWhatsappLoginRequiredVariant} from './utils.js'

const DetailFormContactSellerModalWrapper = lazy(() => import('./DetailFormContactSellerModalWrapper.js'))

const CustomChildren = ({loader}) => {
  const {i18n} = useContext(Context)

  return (
    <div className="mt-Detail-whatsappSpinner">
      {loader}
      <span>{i18n.t('WHATSAPP_LEAD_BUTTON.SPINNER_TEXT')}</span>
    </div>
  )
}

const DetailFormContactSellerWrapper = ({
  ad,
  adSpecificationsText,
  adType,
  alertSearch,
  contactFormWrapperRef,
  dealer,
  financingData,
  initialLeadEnrichmentValue,
  isSubscriptionLead,
  handleOnFieldChange,
  handleScrollToB2cFinancingPanel,
  handleScrollToFinancingTerms,
  hasFinancing = false,
  hidePhone,
  leadData,
  multiprovince = '',
  onCloseModal,
  onRedirectTo = () => {},
  pageName,
  provinceName,
  relatedAds,
  seller,
  sellerPhone,
  showModal,
  showSearchSave,
  showTargetBuyerOptions,
  targetBuyer,
  userData,
  vehicleInfo,
  onCreateAlert = () => {},
  enableAlertCreation,
  enableLeadEnrichment,
  isAdReservable,
  subscriptionPriceOptionSelected,
  // ff_web_lead_experience_whatsapp_label_buttons — la página es la única que decide el experimento
  whatsappLeadExperienceVariant = null
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...reducerInitialState,
    leadData,
    userData
  })

  const [isWhatsappLoading, setIsWhatsappLoading] = useState(false)
  const [isWhatsappError, setIsWhatsappError] = useState(false)
  const sellerId = seller?.isDealer ? seller.dealer.sellerMessagingId : seller?.user.userId

  const {domain, i18n, userAgent} = useContext(Context)
  const {decide} = useDecisionCallback()

  const isSafari = /^(?!.*(?:Chrome|CriOS|Chromium|Edg|OPR)).*Safari/i.test(userAgent)

  const {isLogged} = useContext(AuthenticationContext)
  const {triggerLoginModal} = useContext(LoginModalContext)
  const {track: analyticsTrack} = useAnalyticsTrack()
  const {track: criteoTrack} = useCriteoTrack()

  const config = domain.get('config')
  const isZipCodeEnabled = config.get('form').ZIPCODE_ENABLED
  const {OFFER_TYPE_NEW} = config.get('search')
  const {LOGIN_SOURCE_ID_WHATSAPP_LEAD} = config.get('login')
  const isMobile = config.get('isMobile')

  const {offerTypeId} = adType
  const {hasCallTrackingService} = seller.dealer
  const isNewVehicle = offerTypeId === OFFER_TYPE_NEW

  const handleModalClose = () => {
    onCloseModal()
    dispatch({
      type: reducerActions.SET_SHOW_MODAL,
      payload: {
        showModal: false
      }
    })
  }

  const createWhatsappIntent = () => domain.get('create_whatsapp_intents_user_use_case').execute({resourceId: ad.id})

  // Safari blocks window.open after await — open empty tab synchronously first, then navigate it
  const openWhatsappTab = () => (isSafari ? window.open('', '_blank') : null)
  const navigateToWhatsapp = (tab, chatDeepLink) => {
    if (tab) {
      tab.location.href = chatDeepLink
    } else {
      window.open(chatDeepLink, '_blank', 'noopener,noreferrer')
    }
  }

  const handleWhatsappLeadTrack = async ({id}) => {
    await analyticsTrack({
      event: 'Lead Triggered',
      data: {
        lead_type: config.get('tracking').LEAD_TYPES.DETAIL_AD_REPLIED_WHATSAPP,
        ...leadData,
        hit_information3: id
      }
    })
  }

  const handleWhatsappClickTrack = async () => {
    await analyticsTrack({
      event: 'Whatsapp Button Clicked',
      data: {
        hit_origin: 'detail'
      }
    })
  }

  const handleWhatsappDeepLink = async () => {
    if (!isSafari) setIsWhatsappLoading(true)
    const tab = openWhatsappTab()

    if (isSafari && (!tab || tab.closed || typeof tab.closed === 'undefined')) {
      setIsWhatsappLoading(false)
      return
    }

    const [error, chatResponse] = await createWhatsappIntent()

    if (chatResponse?.chatDeepLink) {
      const {id} = chatResponse
      await handleWhatsappLeadTrack({id})

      if (isSafari) {
        navigateToWhatsapp(tab, chatResponse.chatDeepLink)
      } else {
        setTimeout(() => {
          setIsWhatsappLoading(false)
          navigateToWhatsapp(tab, chatResponse.chatDeepLink)
        }, 3000)
      }

      return
    }

    tab?.close()
    setIsWhatsappLoading(false)
    if (error) setIsWhatsappError(true)
  }

  const handleWhatsappIntent = useCallback(async () => {
    // ff_lead_experience_login_whatsapp sigue siendo el kill-switch: encenderlo desactiva el login
    // en todas las variantes sin deploy
    const {enabled: isSkipLoginEnabled} = decide('ff_lead_experience_login_whatsapp')
    // ff_web_lead_experience_whatsapp_label_buttons — solo la variante `a` exige login
    const isLoginRequired =
      !isLogged && !isSkipLoginEnabled && getIsWhatsappLoginRequiredVariant(whatsappLeadExperienceVariant)

    await handleWhatsappClickTrack()

    const {
      HIT_ORIGINS: {LEAD_WHATSAPP: hitOriginLeadWhatsapp}
    } = config.get('tracking')

    // ff_lead_experience_login_whatsapp
    if (isLoginRequired) {
      triggerLoginModal({
        modalProps: {
          trackingOrigin: hitOriginLeadWhatsapp,
          source: LOGIN_SOURCE_ID_WHATSAPP_LEAD,
          onLogin: handleWhatsappDeepLink
        }
      })
      return
    }

    handleWhatsappDeepLink()
  }, [ad.id, isLogged, whatsappLeadExperienceVariant]) // eslint-disable-line

  const handleContactByMail = useCallback(
    async ({
      isFinancingLead = false,
      userHasBeenRegistered = false,
      autoGeneratedPassword = null,
      messageContactId = ''
    }) => {
      analyticsTrack({
        event: 'Lead Triggered',
        data: {
          lead_type: isFinancingLead
            ? 'Financed Purchase Email'
            : config.get('tracking').LEAD_TYPES.DETAIL_AD_REPLIED_EMAIL,
          hit_information3: messageContactId,
          ...leadData
        }
      })
      criteoTrack({
        event: criteoTrackEvents.TRACK_TRANSACTION,
        eventData: {adId: ad.id}
      })
      dispatch({
        type: reducerActions.HANDLE_LEAD,
        payload: {
          showModal: true,
          contactType: listRelatedAdsContactTypes.email,
          userHasBeenRegistered,
          autoGeneratedPassword
        }
      })
    },
    [leadData] // eslint-disable-line
  )

  const handleRedirectTo = url => onRedirectTo(url)

  const handleLeadPhoneCall = () =>
    dispatch({
      type: reducerActions.HANDLE_LEAD,
      payload: {
        showModal: true,
        contactType: listRelatedAdsContactTypes.phoneCall
      }
    })

  useEffect(() => {
    showModal &&
      dispatch({
        type: reducerActions.HANDLE_LEAD,
        payload: {
          showModal: true,
          contactType: listRelatedAdsContactTypes.phoneCall
        }
      })
  }, [showModal])

  return (
    <>
      <div className="mt-Detail-formContactSellerWrapper" ref={contactFormWrapperRef}>
        <FormContactSeller
          ad={ad}
          adId={ad.id}
          buttonCallSeller={
            !hidePhone && (
              <LeadPhoneCall
                adId={ad.id}
                color="primary"
                contractId={seller.contractId}
                design="outline"
                extendedLeadTrackingData={leadData}
                hasCallTrackingService={hasCallTrackingService}
                hasCriteoEvents
                isFullWidth
                isLoggedUser={Boolean(userData)}
                offerTypeId={offerTypeId}
                onLeadPhoneCall={handleLeadPhoneCall}
                pageName={pageName}
                phone={sellerPhone}
                size="large"
                target={leadPhoneCallTarget.vehicle}
                type={leadPhoneCallType.button}
                multiprovince={multiprovince}
              />
            )
          }
          buttonWhatsapp={
            isMobile &&
            dealer?.hasWhatsappProduct && (
              <AtomButton
                type="button"
                design={atomButtonDesigns.OUTLINE}
                shape={atomButtonShapes.CIRCULAR}
                onClick={handleWhatsappIntent}
                size={atomButtonSizes.LARGE}
                leftIcon={<IconWhatsApp size="medium" />}
                fullWidth
              >
                {i18n.t('WHATSAPP_LEAD_BUTTON.LABEL')}
              </AtomButton>
            )
          }
          contractId={seller.contractId}
          dealer={dealer}
          handleScrollToB2cFinancingPanel={handleScrollToB2cFinancingPanel}
          isZipCodeEnabled={isZipCodeEnabled && isNewVehicle}
          handleOnFieldChange={handleOnFieldChange}
          handleScrollToFinancingTerms={handleScrollToFinancingTerms}
          hasFinancingData={hasFinancing}
          isLoggedUser={Boolean(userData)}
          isSellerBlocked={seller.isSellerBlocked || false}
          isSubscriptionLead={isSubscriptionLead}
          leadData={state.leadData}
          onContact={handleContactByMail}
          offerTypeId={offerTypeId}
          pageName={pageName}
          price={{financedPrice: ad.financedPrice, price: ad.price}}
          province={provinceName}
          redirectTo={handleRedirectTo}
          search={alertSearch}
          showSearchSave={showSearchSave}
          showTargetBuyerOptions={showTargetBuyerOptions}
          targetBuyer={targetBuyer}
          vehicle={adSpecificationsText}
          userData={userData}
          enableAlertCreation={enableAlertCreation}
          onCreateAlert={onCreateAlert}
          enableLeadEnrichment={enableLeadEnrichment}
          initialLeadEnrichmentValue={initialLeadEnrichmentValue}
          sellerId={sellerId}
          isAdReservable={isAdReservable}
          subscriptionPriceOptionSelected={subscriptionPriceOptionSelected}
          hideButtonsSeparator
          enableAuthBeforeLead={seller?.isDealer}
        />
      </div>
      {(state.showModal || showModal) && (
        <Suspense fallback={<div />}>
          <DetailFormContactSellerModalWrapper
            ad={ad}
            adType={adType}
            autoGeneratedPassword={state.autoGeneratedPassword}
            contactType={state.contactType}
            leadData={leadData}
            offerTypeId={offerTypeId}
            onChangePasswordModalClose={() => dispatch({type: reducerActions.CLEAR_CHANGE_PASSWORD_MODAL})}
            onModalClose={handleModalClose}
            relatedAds={relatedAds}
            seller={seller}
            showModal={state.showModal}
            userHasBeenRegistered={state.userHasBeenRegistered}
            userData={userData}
            vehicleInfo={vehicleInfo}
          />
        </Suspense>
      )}
      {isWhatsappLoading && (
        <AtomSpinner type={atomSpinnerTypes.FULL} overlayType={atomSpinnerOverlayTypes.DARK}>
          <CustomChildren />
        </AtomSpinner>
      )}
      {isWhatsappError && (
        <MoleculeNotification
          autoClose={moleculeNotificationAutoClose.manual}
          icon={<IconError size={ATOM_ICON_SIZES.medium} />}
          onClose={() => setIsWhatsappError(false)}
          position={moleculeNotificationPosition.top}
          show={isWhatsappError}
          showCloseButton
          showLeftIconMobile
          type={moleculeNotificationTypes.error}
          variation={moleculeNotificationVariations.negative}
        >
          <div className="mt-Detail-whatsappErrorNotification-content">
            <p className="mt-Detail-whatsappErrorNotification-title">{i18n.t('WHATSAPP_LEAD_ERROR.TITLE')}</p>
            <p className="mt-Detail-whatsappErrorNotification-body">{i18n.t('WHATSAPP_LEAD_ERROR.BODY')}</p>
          </div>
        </MoleculeNotification>
      )}
    </>
  )
}

export default memo(DetailFormContactSellerWrapper)
